import React, { useEffect, useState } from "react";
import { useGameMachine } from "./hooks/useGameMachine.js";
import { useLayout } from "./hooks/useLayout.js";
import { CombatLog } from "./components/CombatLog.js";
import { ReactionPrompt } from "./components/ReactionPrompt.js";
import { DialogueBox } from "./components/DialogueBox.js";
import { PartyBar } from "./components/PartyBar.js";
import { MiniMap } from "./components/MiniMap.js";
import { MainMenu } from "./components/MainMenu.js";
import { DesktopHUD } from "./components/DesktopHUD.js";
import { EnemyMeter } from "./components/EnemyMeter.js";
import { DamagePopups } from "./components/DamagePopups.js";
import { DifficultyChip } from "./components/DifficultyChip.js";
import { ShopScreen } from "./components/ShopScreen.js";
import { ContainerLootModal } from "./components/ContainerLootModal.js";
import { LootPileModal } from "./components/LootPileModal.js";
import { CampStashScreen } from "./components/CampStashScreen.js";
import { LockMenu } from "./components/LockMenu.js";
import { NewGameFlow } from "./components/NewGameFlow.js";
import { LaunchScreen } from "./components/LaunchScreen.js";
import { LaunchAdventurePicker } from "./components/LaunchAdventurePicker.js";
import { SavePicker } from "./components/SavePicker.js";
import { EscChip } from "./components/EscChip.js";
import { NavCluster } from "./components/NavCluster.js";
import { SpellChooser } from "./components/SpellChooser.js";
import { SpellArmingToast } from "./components/SpellArmingToast.js";
import { SpellTargetingBoard } from "./components/SpellTargetingBoard.js";
import { PartyWipeModal } from "./components/PartyWipeModal.js";
import { CampPrompt } from "./components/CampPrompt.js";
import { AsiModal } from "./components/AsiModal.js";
import { EventBus } from "../engine/EventBus.js";
import { PartyStore } from "../store/PartyStore.js";
import type { AbilityScores } from "../engine/types/entity.js";

/**
 * Root UI component.
 * Renders nothing over the Three.js canvas while the game is loading.
 * Once exploring/combat, mounts the HUD layer.
 *
 * All children use `pointer-events: auto` selectively — the root div is
 * `pointer-events: none` so unhandled clicks fall through to the canvas.
 */
export function App() {
  const [snapshot, send] = useGameMachine();
  const layout = useLayout();
  const [campStashOpen, setCampStashOpen] = useState(false);

  // W10 — ASI modal deps: read scores from PartyStore, write them back.
  const asiGetCharacter = (instanceId: string) => {
    const m = PartyStore.getMember(instanceId);
    return m ? { instanceId: m.instanceId, name: m.name, abilities: m.abilities } : null;
  };
  const asiOnConfirm = (instanceId: string, abilities: AbilityScores) => {
    PartyStore.updateMember(instanceId, { abilities });
    EventBus.emit("ui:party_update", {});
  };

  useEffect(() => {
    const off = EventBus.on("ui:camp_stash_open", () => setCampStashOpen(true));
    return off;
  }, []);

  // Mirror the layout mode onto <body> so index.html's CSS can restyle
  // ancestor-level chrome (most importantly: shrink #game to the top
  // region in mobile-portrait so the HUD dock shows through below).
  // Dispatch a resize event on change so SceneManager picks up the new
  // canvas dimensions and reprojects.
  useEffect(() => {
    document.body.dataset.layoutMode = layout;
    // Defer to next frame so the browser applies the new CSS
    // (canvas clientHeight changes when #game shrinks to 56vh) before
    // SceneManager reads it on the resize handler.
    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => {
      cancelAnimationFrame(raf);
      delete document.body.dataset.layoutMode;
    };
  }, [layout]);

  if (snapshot.matches("launch")) {
    return <LaunchScreen />;
  }

  if (snapshot.matches("adventurePicker")) {
    return <LaunchAdventurePicker />;
  }

  if (snapshot.matches("savePicker")) {
    return <SavePicker />;
  }

  if (snapshot.matches("characterCreation")) {
    return <NewGameFlow />;
  }

  if (snapshot.matches("loading")) {
    return (
      <div style={{ color: "white", padding: 16, pointerEvents: "none" }}>
        Loading…
      </div>
    );
  }

  if (snapshot.matches("error")) {
    return (
      <div style={{ color: "red", padding: 16, pointerEvents: "none" }}>
        Failed to load: {snapshot.context.loadError}
      </div>
    );
  }

  if (snapshot.matches("victory")) {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.8)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          pointerEvents: "auto",
          zIndex: 300,
        }}
      >
        <div
          style={{
            color: "#ffd700",
            fontFamily: "monospace",
            fontSize: 36,
            letterSpacing: 4,
            textShadow: "0 0 20px rgba(255,215,0,0.8)",
          }}
        >
          VICTORY
        </div>
        <div
          style={{
            color: "#ccc",
            fontFamily: "monospace",
            fontSize: 14,
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          You have conquered the dungeon.
        </div>
      </div>
    );
  }

  if (layout === "desktop") {
    return (
      <div
        data-layout-mode={layout}
        style={{ pointerEvents: "none", width: "100%", height: "100%" }}
      >
        <DesktopHUD
          showMenu={snapshot.matches("menu")}
          onCloseMenu={() => send({ type: "CLOSE_MENU" })}
        />
        <EnemyMeter />
        <DamagePopups />
        <DifficultyChip />
        <ShopScreen />
        <ContainerLootModal />
        <LootPileModal />
        <LockMenu />
        <SpellChooser />
        <SpellArmingToast />
        <SpellTargetingBoard />
        <PartyWipeModal onReturnToMenu={() => send({ type: "SHOW_LAUNCH" })} />
        <CampPrompt />
        <AsiModal getCharacter={asiGetCharacter} onConfirm={asiOnConfirm} />
        {campStashOpen && <CampStashScreen onClose={() => setCampStashOpen(false)} />}
      </div>
    );
  }

  // mobile-portrait + mobile-landscape share the same HUD tree for now.
  // Per-mode layout tuning happens via CSS on `[data-layout-mode=...]`.
  return (
    <div
      data-layout-mode={layout}
      style={{ pointerEvents: "none", width: "100%", height: "100%" }}
    >
      <PartyBar />
      <MiniMap />
      <CombatLog />
      <ReactionPrompt />
      <DialogueBox />
      <EnemyMeter />
      <DamagePopups />
      <DifficultyChip />
      <ShopScreen />
      <ContainerLootModal />
      <LootPileModal />
      <LockMenu />
      <CampPrompt />
      <AsiModal getCharacter={asiGetCharacter} onConfirm={asiOnConfirm} />
      <SpellChooser />
      <SpellArmingToast />
      <SpellTargetingBoard />
      <PartyWipeModal onReturnToMenu={() => send({ type: "SHOW_LAUNCH" })} />
      <EscChip />
      {!snapshot.matches("menu") && <NavCluster />}
      {snapshot.matches("menu") && (
        <MainMenu onClose={() => send({ type: "CLOSE_MENU" })} />
      )}
    </div>
  );
}
